import { useState } from 'react';

import { Check, WalletIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PaymentDetailsEntry } from '@/modules/settings/types/payment-details';
import { SettingsRoutes } from '@/router/routes';

/** Show the search input only when the list is long enough for scanning to hurt. */
const SEARCH_VISIBILITY_THRESHOLD = 7;

/**
 * Per-invoice payment-details selection, owned by the generate-invoices page.
 * Selections are stored as overrides on top of the organization default, so an
 * untouched row always follows the current default.
 */
export interface PaymentDetailsSelector {
  options: PaymentDetailsEntry[];
  defaultId: number | null;
  getSelected: (projectId: string) => number | null;
  hasOverride: (projectId: string) => boolean;
  onSelect: (projectId: string, paymentDetailsId: number | null) => void;
}

interface PaymentDetailsCellProps {
  projectId: string;
  selector: PaymentDetailsSelector;
}

/**
 * Wallet icon on a QuickBooks-connected parent project row of the Generate
 * Invoices table. Opens a popover to pick the payment-details entry attached
 * to that project's invoice (or None).
 */
export const PaymentDetailsCell = ({ projectId, selector }: PaymentDetailsCellProps) => {
  const { t } = useTranslation('authenticated.invoices', { keyPrefix: 'new.paymentDetails' });
  const [open, setOpen] = useState(false);

  const selectedId = selector.getSelected(projectId);
  const selectedEntry =
    selectedId !== null ? selector.options.find(entry => entry.id === selectedId) : undefined;
  const isUntouchedDefault =
    selectedEntry !== undefined &&
    selectedId === selector.defaultId &&
    !selector.hasOverride(projectId);

  const tooltipText = selectedEntry
    ? t(isUntouchedDefault ? 'tooltipSelectedDefault' : 'tooltipSelected', {
        name: selectedEntry.name,
      })
    : t('tooltipNone');

  const handleSelect = (paymentDetailsId: number | null) => {
    selector.onSelect(projectId, paymentDetailsId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="xxs"
              className="shrink-0"
              onClick={event => event.stopPropagation()}
            >
              <WalletIcon
                size={16}
                className={cn(selectedEntry ? 'text-foreground' : 'text-muted-foreground')}
              />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>{tooltipText}</TooltipContent>
        </TooltipPortal>
      </Tooltip>
      <PopoverContent className="w-80 p-1" align="start" onClick={event => event.stopPropagation()}>
        <Command>
          {selector.options.length > SEARCH_VISIBILITY_THRESHOLD && (
            <CommandInput placeholder={t('searchPlaceholder')} />
          )}
          <CommandList>
            <CommandEmpty>{t('empty')}</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={() => handleSelect(null)}>
                <span className="text-muted-foreground">{t('noneOption')}</span>
                <Check
                  className={cn(
                    'ml-auto h-4 w-4',
                    selectedId === null ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </CommandItem>
              {selector.options.map(entry => (
                <CommandItem
                  key={entry.id}
                  value={entry.name}
                  onSelect={() => handleSelect(entry.id)}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">{entry.name}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          {entry.details.split('\n')[0]}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent side="right" className="max-w-xs whitespace-pre-line">
                        {entry.details}
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                  <Check
                    className={cn(
                      'ml-auto h-4 w-4 shrink-0',
                      selectedId === entry.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className="border-t pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground"
            onClick={() => window.open(SettingsRoutes.Finance, '_blank', 'noopener,noreferrer')}
          >
            {t('manageLink')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
